/**
 * Decides how this page reads each monitoring frame by the schema its LN2E
 * envelope names.
 *
 * A frame written with this bundle's own schema is read as today, with no
 * other work. For another schema, which is a server built from other schemas
 * than this bundle, the server is asked once:
 *
 * 1. for the schema itself (`fetchSchema`), checked against its SHA-256, for
 *    its root type and where it came from;
 * 2. for the verdict on reading it with this bundle's schema of that root type
 *    (`schemaVerdict`).
 *
 * Then:
 * - **exact** or **proven** (it reads the same): read as today;
 * - **extended** (it only added values): read, except the messages that hold a
 *   value this bundle has no name for (`GainedValueCheck`);
 * - anything else (it changed what this bundle reads): not read, **degraded**;
 * - no answer (the server does not know this bundle's schema): read the
 *   messages whose every enum and union value this bundle knows, as the
 *   bundle's own decoder verifies them (**unverified**).
 *
 * Frames that arrive while a schema is being looked up wait, in order, up to a
 * bound, and are then read as decided. Every frame of a schema not this
 * bundle's is recorded for the admin skew banner.
 */

import { loadSchema, type ReflectionSchema } from "../reflection/reflection";
import { schemaVerdict, type SchemaVerdict } from "../api/apiVerdicts";
import {
  BATCHED_FRONT_END_MESSAGE_SCHEMA_HASH,
  FRONT_END_MESSAGE_SCHEMA_HASH,
} from "../schemaHashes";
import { isSchemaHash } from "../schemaHeaders";
import { noteSchemaSkew, noteSkewFrame } from "../schemaSkew";
import { GainedValueCheck, type GainedValues } from "./gainedValues";
import { fetchSchema, type FetchedSchema } from "./schemaFetch";

/** The root types of monitoring frames. */
export const FRONT_END_MESSAGE = "FrontEnd.FrontEndMessage";
export const BATCHED_FRONT_END_MESSAGE = "BatchedFrontEnd.BatchedFrontEndMessage";

/** The field of a batch that holds its messages. */
const BATCH_MESSAGES = "messages";

/** Bytes before a size-prefixed FlatBuffer's root offset. */
const SIZE_PREFIX = 4;

/** How long an unanswered schema is read as unverified before the server is asked again. */
const RETRY_AFTER_MS = 5 * 60 * 1000;

/** How many frames may wait for their schema's lookup before the oldest is dropped. */
const MAX_WAITING = 256;

/** One LN2E-enveloped monitoring frame. */
export interface MonitoringFrame {
  /** The SHA-256 its envelope names, as 64 lower-case hex digits. */
  schemaHash: string;
  /** The FlatBuffer it carries: a size-prefixed batch, or one FrontEndMessage. */
  payload: Uint8Array;
}

/** How a frame may be read. */
export type FrameReading =
  /** All of it, as today. */
  | { read: "all" }
  /** All but these messages of its batch (indices into its `messages`). */
  | { read: "except"; skip: ReadonlySet<number> }
  /** None of it. */
  | { read: "none"; reason: string };

/** What the lookup decided for a schema. */
interface Decision {
  /** The schema discovery outcome frames resolve as: `exact`, `proven`, `extended`, … */
  outcome: string;
  /** The schema's root type; empty when the server could not say. */
  rootType: string;
  provenance?: string;
  signature?: FetchedSchema["signature"];
  /** Set for frames that are read; checks their messages when present. */
  check?: GainedValueCheck;
  /** Set for frames that are not read. */
  refusal?: string;
  expires: number;
}

/** This bundle's own schemas, by SHA-256: frames that need no lookup. */
const OWN_SCHEMAS = new Map([
  [FRONT_END_MESSAGE_SCHEMA_HASH, FRONT_END_MESSAGE],
  [BATCHED_FRONT_END_MESSAGE_SCHEMA_HASH, BATCHED_FRONT_END_MESSAGE],
]);

/** This bundle's schema of each monitoring root type, loaded only when a frame needs it. */
const OWN_SCHEMA_BYTES: Record<string, () => Promise<Uint8Array>> = {
  [FRONT_END_MESSAGE]: async () =>
    (await import("../generated/front-end/front-end-message-bfbs")).bfbsData,
  [BATCHED_FRONT_END_MESSAGE]: async () =>
    (await import("../generated/batched-front-end/batched-front-end-message-bfbs")).bfbsData,
};

const ownSchemas = new Map<string, Promise<ReflectionSchema>>();

/** This bundle's schema of `rootType`, parsed once. */
function ownSchema(rootType: string): Promise<ReflectionSchema> {
  let schema = ownSchemas.get(rootType);
  if (!schema) {
    schema = OWN_SCHEMA_BYTES[rootType]().then(loadSchema);
    ownSchemas.set(rootType, schema);
  }
  return schema;
}

/** The own-schema hash of a monitoring root type. */
function ownHashOf(rootType: string): string | undefined {
  return [...OWN_SCHEMAS].find(([, root]) => root === rootType)?.[0];
}

/** Whether a frame's payload is a size-prefixed batch rather than one FrontEndMessage. */
function isSizePrefixed(payload: Uint8Array): boolean {
  if (payload.length < SIZE_PREFIX * 2) {
    return false;
  }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return view.getUint32(0, true) === payload.length - SIZE_PREFIX;
}

/** Every enum and union of `schema`, for checking a frame of a schema nobody vouched for. */
function everyEnum(schema: ReflectionSchema): GainedValues[] {
  return schema.enums().map((reflected) => ({
    enum: reflected.name(),
    union: reflected.isUnion(),
    values: [],
  }));
}

const DECODABLE = new Set(["exact", "proven"]);

/**
 * What the server's verdict decides for a schema. Its frames are checked with
 * this bundle's schema of `planRoot`: the schema's own root type, or the batch
 * when the server could not say what that is.
 */
async function decide(
  fetched: FetchedSchema | null,
  verdict: SchemaVerdict | null,
  planRoot: string,
): Promise<Omit<Decision, "expires">> {
  const known = {
    rootType: fetched?.rootType ?? "",
    provenance: fetched?.provenance,
    signature: fetched?.signature,
  };
  if (verdict === null) {
    const schema = await ownSchema(planRoot);
    return {
      ...known,
      outcome: "unverified",
      check: GainedValueCheck.plan(schema, everyEnum(schema)) ?? undefined,
    };
  }
  if (DECODABLE.has(verdict.outcome)) {
    return { ...known, outcome: verdict.outcome };
  }
  if (verdict.outcome === "extended" && verdict.must_understand.every((item) => item.value)) {
    // A value this bundle must understand and lacks is a gained value too, so
    // the check drops the messages that hold one.
    const check = GainedValueCheck.plan(await ownSchema(planRoot), verdict.gained_values);
    return { ...known, outcome: "extended", check: check ?? undefined };
  }
  const finding = verdict.incompatibilities[0];
  const field = verdict.must_understand.find((item) => !item.value);
  let why = `the server's schema relates to this page's as ${verdict.kind}`;
  if (field) {
    why = `${field.owner}.${field.name} must be understood`;
  } else if (finding) {
    why = `${finding.path}: ${finding.detail || finding.reason}`;
  }
  return { ...known, outcome: "degraded", refusal: why };
}

/** How a frame of a decided schema may be read, by its messages. */
function readingOf(payload: Uint8Array, decision: Decision): FrameReading {
  const { check, rootType } = decision;
  if (!check) {
    return { read: "all" };
  }
  const batch =
    rootType === BATCHED_FRONT_END_MESSAGE || (rootType === "" && isSizePrefixed(payload));
  if (batch) {
    const skip = check.unknownElements(
      payload.subarray(SIZE_PREFIX),
      BATCHED_FRONT_END_MESSAGE,
      BATCH_MESSAGES,
    );
    if (skip === null) {
      return { read: "none", reason: "it is not a batch this page can read" };
    }
    return skip.size === 0 ? { read: "all" } : { read: "except", skip };
  }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const unknown =
    payload.length < SIZE_PREFIX ||
    check.holdsUnknown(payload, view.getUint32(0, true), FRONT_END_MESSAGE);
  return unknown
    ? { read: "none", reason: "it holds a value this page has no name for" }
    : { read: "all" };
}

/**
 * Looks schemas up and reads frames by them. One gate serves a page: every
 * socket's frames are admitted through it.
 */
export class MonitoringSchemaGate {
  private readonly decisions = new Map<string, Decision>();

  private readonly lookups = new Map<string, Promise<void>>();

  private readonly waiting: {
    frame: MonitoringFrame;
    read: (reading: FrameReading) => void;
  }[] = [];

  private dropped = 0;

  private readonly baseUrl: string;

  private readonly authorization: () => Promise<string | undefined>;

  /**
   * @param baseUrl       The schema API's base URL (the API the page talks to).
   * @param authorization The value of the API's `authorization` header.
   */
  constructor(baseUrl: string, authorization: () => Promise<string | undefined>) {
    this.baseUrl = baseUrl;
    this.authorization = authorization;
  }

  /** Resolves once every lookup under way is decided and the frames waiting on it are read. */
  async settled(): Promise<void> {
    if (this.lookups.size === 0) {
      this.drain();
      return;
    }
    await Promise.all(this.lookups.values());
    await this.settled();
  }

  /** How many frames were dropped because too many waited for a lookup. */
  get droppedWhileWaiting(): number {
    return this.dropped;
  }

  /**
   * Calls `read` for `frame` with how it may be read: at once for a frame of
   * this bundle's own schema or of one already decided, otherwise once its
   * schema is looked up. Frames are read in the order they are admitted.
   */
  admit(frame: MonitoringFrame, read: (reading: FrameReading) => void): void {
    if (this.waiting.length === 0) {
      const reading = this.readingNow(frame);
      if (reading !== null) {
        read(reading);
        return;
      }
    }
    if (this.waiting.length >= MAX_WAITING) {
      this.waiting.shift();
      this.dropped += 1;
    }
    this.waiting.push({ frame, read });
    this.lookUp(frame.schemaHash).then(() => this.drain());
  }

  /** How `frame` may be read now, or null when its schema is still to be decided. */
  private readingNow(frame: MonitoringFrame): FrameReading | null {
    if (OWN_SCHEMAS.has(frame.schemaHash)) {
      return { read: "all" };
    }
    const decision = this.decisions.get(frame.schemaHash);
    if (!decision) {
      return null;
    }
    if (decision.expires <= Date.now() && !this.lookups.has(frame.schemaHash)) {
      // Ask again without holding frames back: they are read as decided until
      // the new answer comes.
      this.lookUp(frame.schemaHash).then(() => this.drain());
    }
    const typeName = decision.rootType || "(unknown root type)";
    noteSchemaSkew({
      channel: "ws",
      schema: frame.schemaHash,
      typeName,
      outcome: decision.outcome,
      provenance: decision.provenance,
      signature: decision.signature,
    });
    const reading: FrameReading =
      decision.refusal === undefined
        ? readingOf(frame.payload, decision)
        : { read: "none", reason: decision.refusal };
    noteSkewFrame({
      schema: frame.schemaHash,
      typeName: decision.rootType,
      payload: frame.payload,
      read: reading.read,
    });
    return reading;
  }

  /** Reads the waiting frames, in order, up to the first whose schema is still undecided. */
  private drain(): void {
    while (this.waiting.length > 0) {
      const [next] = this.waiting;
      const reading = this.readingNow(next.frame);
      if (reading === null) {
        return;
      }
      this.waiting.shift();
      next.read(reading);
    }
  }

  /** Looks `hash` up once at a time; resolves when it is decided. */
  private lookUp(hash: string): Promise<void> {
    let lookup = this.lookups.get(hash);
    if (!lookup) {
      lookup = this.decideSchema(hash).finally(() => this.lookups.delete(hash));
      this.lookups.set(hash, lookup);
    }
    return lookup;
  }

  /** Decides `hash`; a lookup that fails decides that its frames are not read, for a while. */
  private async decideSchema(hash: string): Promise<void> {
    try {
      await this.decideSchemaOrThrow(hash);
    } catch {
      this.decisions.set(hash, {
        outcome: "degraded",
        rootType: "",
        refusal: "this page could not check its schema",
        expires: Date.now() + RETRY_AFTER_MS,
      });
    }
  }

  private async decideSchemaOrThrow(hash: string): Promise<void> {
    const authorization = await this.authorization().catch(() => undefined);
    const fetched = isSchemaHash(hash)
      ? await fetchSchema(this.baseUrl, hash, authorization)
      : null;
    const rootType = fetched?.rootType ?? "";
    const reader = ownHashOf(rootType);
    let decision: Omit<Decision, "expires">;
    if (fetched !== null && reader === undefined) {
      decision = {
        outcome: "degraded",
        rootType,
        provenance: fetched.provenance,
        signature: fetched.signature,
        refusal: `its root type ${rootType || "(none)"} is not a monitoring message`,
      };
    } else {
      // Unanswered, a frame is read as one the bundle's batch decoder takes: the
      // monitoring socket sends batches.
      const root = rootType || BATCHED_FRONT_END_MESSAGE;
      const verdict =
        reader === undefined
          ? null
          : await schemaVerdict(this.baseUrl, hash, reader, undefined, authorization);
      decision = await decide(fetched, verdict, root);
    }
    const answered = decision.outcome !== "unverified" && fetched !== null;
    this.decisions.set(hash, {
      ...decision,
      expires: answered ? Number.POSITIVE_INFINITY : Date.now() + RETRY_AFTER_MS,
    });
  }
}
